import {
    ReadonlyMat3,
    ReadonlyMat4,
    ReadonlyVec2,
    ReadonlyVec3,
    mat3,
    mat4,
    quat,
    vec2,
    vec3,
} from 'gl-matrix';
import { WindowLevel } from '~/domain/patient/windowLevel';
import { VertexArrayObject } from '~/engine/core/utils/VertexArrayObject';
import { ImageSlice } from '../../../domain/dicom/imageSlice';
import { Image3D } from '../../../domain/patient/image3d';
import { gl } from '../../Engine';
import { BoundingBox } from '../common/BoundingBox';
import { Edge } from '../common/Edge';
import { EnvironmentObject } from '../common/EnvironmentObject';
import { Grid } from '../common/Grid';
import { RotatedBoundingBox } from '../common/RotatedBoundingBox';
import { SliceData } from '../common/slicemesh/SliceData';
import { SliceSource } from '../common/slicemesh/SliceSource';
import { Vertex, VerticesToArrays } from '../common/slicemesh/Vertex';
import { SliceMesh } from '../common/slicemesh/SliceMesh';

/**
 * {@link ImageObject} represents a 3D volume data.
 */
export class ImageObject extends EnvironmentObject {
    /**
     * Source series UID
     */
    public readonly seriesInstanceUID: string;

    private readonly slices: ImageObjectSliceStorage;

    /**
     * The {@link WebGLTexture texture} in which the scan's texture data is stored.\
     * This may be null so unit tests don't require {@link WebGL2RenderingContext WebGL context}.
     */
    private readonly texture: WebGLTexture | null;

    private textureData: Float32Array | null = null;

    /**
     * Transformatio that may be applied to convert point from PatientSpace to ImageSpace.\
     * ImageSpace coordinates are in between -1 and 1 for patient space coordinates within the Image.
     */
    private readonly _patientToNormalizedImage: mat4;

    private _minValue: number;

    public get windowLevel(): WindowLevel | undefined {
        return this.parent.windowLevel;
    }

    public set windowLevel(windowLevel: WindowLevel) {
        this.parent.windowLevel = windowLevel;
    }

    private readonly _colorMask: vec3;

    private _alpha: number;

    private readonly _headFirstProne: boolean;

    public get headFirstProne(): boolean {
        return this._headFirstProne;
    }

    private _parent!: Image3D;
    public get parent(): Image3D {
        return this._parent;
    }

    public static fromImage3D(
        image3d: Image3D,
        imageSlice: ImageSlice
    ): ImageObject {
        const img = new ImageObject(
            image3d,
            imageSlice,
            image3d.dicomUid,
            image3d.minValue,
            image3d.windowLevel
        );
        const float32 = new Float32Array(image3d.pixelData);
        img.updateTexture(float32, image3d.grid);
        img.slices.resetFromImage3D(image3d);
        img._parent = image3d;
        return img;
    }

    public updateTextureFromImage3D() {
        this.updateTexture(
            new Float32Array(this.parent.pixelData),
            this.parent.grid
        );
    }

    public constructor(
        image3d: Image3D,
        imageSlice: ImageSlice,
        seriesUID: string,
        minValue: number,
        windowLevel: WindowLevel | null = null
    ) {
        super();
        this.seriesInstanceUID = seriesUID;
        this._minValue = minValue;
        this.textureData = new Float32Array(imageSlice.pixelData.slice());
        this._parent = image3d;
        this.seriesInstanceUID = seriesUID;
        this._minValue = minValue;
        this.slices = new ImageObjectSliceStorage(image3d.grid, imageSlice);
        this.texture = this.initializeTexture();
        this.windowLevel = windowLevel || new WindowLevel(400, 20);
        this._colorMask = vec3.fromValues(1, 1, 1);
        this._alpha = 1.0;
        this._grid = image3d.grid;
        this._headFirstProne = imageSlice.headFirstProne;
        // image bounding box is the outer edge of the whole image volume
        const bbMin = this._grid.toWorldSpace(vec3.fromValues(-0.5, -0.5, 0.0));
        const bbMax = this._grid.toWorldSpace(
            vec3.fromValues(
                this._grid.sizeX - 0.5,
                this._grid.sizeY - 0.5,
                this._grid.sizeZ - 1
            )
        );
        const boundingBox = new BoundingBox(bbMin, bbMax);
        this._boundingBox = new RotatedBoundingBox(
            boundingBox,
            this._grid.getOrientation()
        );

        // The _patientToNormalizedImage matrix is used to convert from patient space to normalized image space.
        // now it returns the same values as the SliceMesh implementation
        const sliceMeshAdjustedCenter = vec3.add(
            vec3.create(),
            this._grid.center,
            vec3.fromValues(
                -0.5 * image3d.grid.resX,
                -0.5 * image3d.grid.resY,
                -0.5 * image3d.grid.resZ
            )
        );

        const normalizedImageToPatient = mat4.fromRotationTranslationScale(
            mat4.create(),
            quat.fromMat3(quat.create(), image3d.grid.getOrientation()),
            sliceMeshAdjustedCenter,
            vec3.fromValues(
                image3d.grid.sizeXMM * 0.5,
                image3d.grid.sizeYMM * 0.5,
                (image3d.grid.sizeZMM - image3d.grid.resZ) * 0.5
            )
        );
        this._patientToNormalizedImage = mat4.invert(
            mat4.create(),
            normalizedImageToPatient
        );
    }

    public get sliceSource(): SliceSource<SliceData> {
        return this.slices;
    }

    public get colorMask(): ReadonlyVec3 {
        return this._colorMask;
    }

    public set colorMask(color: ReadonlyVec3) {
        vec3.copy(this._colorMask, color);
    }

    public get alpha(): number {
        return this._alpha;
    }

    public set alpha(alpha: number) {
        this._alpha = alpha;
    }

    public get minValue(): number {
        return this._minValue;
    }

    public get pixelSize(): ReadonlyVec3 {
        return this.parent.grid.getSize();
    }

    /** Z size in pixels */
    public get sizeZ(): number {
        return this._parent.grid.sizeZ;
    }

    public get centerPx(): ReadonlyVec3 {
        return this._parent.grid.center;
    }

    public get grid(): Grid {
        return this.parent.grid;
    }

    public patientCoordsToImageIndices(
        patientCoords: ReadonlyVec3
    ): ReadonlyVec3 {
        return this.parent.grid.toVoxelSpace(patientCoords);
    }

    public normalPositionToSliceIndex(normalPosition: number): number {
        const worldVec = vec3.scale(
            vec3.create(),
            this.parent.grid.getUnitZ(),
            normalPosition
        );
        return this.parent.grid.toVoxelSpace(worldVec)[2];
    }

    public toNormalizedImageSpace(patient: ReadonlyVec3): vec3 {
        return vec3.transformMat4(
            vec3.create(),
            patient,
            this._patientToNormalizedImage
        );
    }

    /** Generate vertices for a slice location */
    private generateVertices(zcoord: number, k: number): Vertex[] {
        const imageGrid = this.parent.grid;

        const vertices = [
            new Vertex(
                imageGrid.toWorldSpace(vec3.fromValues(0, 0, zcoord)),
                vec3.fromValues(0, 0, k)
            ),
            new Vertex(
                imageGrid.toWorldSpace(
                    vec3.fromValues(0, imageGrid.sizeY, zcoord)
                ),
                vec3.fromValues(0, 1, k)
            ),
            new Vertex(
                imageGrid.toWorldSpace(
                    vec3.fromValues(imageGrid.sizeX, imageGrid.sizeY, zcoord)
                ),
                vec3.fromValues(1, 1, k)
            ),
            new Vertex(
                imageGrid.toWorldSpace(
                    vec3.fromValues(imageGrid.sizeX, 0, zcoord)
                ),
                vec3.fromValues(1, 0, k)
            ),
        ];
        const registrationTransformation = this.transformationMatrix;
        if (registrationTransformation) {
            return vertices.map((v) => v.transform(registrationTransformation));
        }
        return vertices;
    }

    // cached image edges for cross section calculation
    private _imageEdges: Edge[] | undefined;
    private get imageEdges(): Edge[] {
        if (!this._imageEdges) {
            const imageGrid = this.parent.grid;
            const vertices = [
                ...this.generateVertices(-0.5, 0),
                ...this.generateVertices(imageGrid.sizeZ - 0.5, 1),
            ];
            this._imageEdges = [];
            this._imageEdges.push(
                new Edge(vertices[0], vertices[1]),
                new Edge(vertices[1], vertices[2]),
                new Edge(vertices[2], vertices[3]),
                new Edge(vertices[3], vertices[0])
            );
            this._imageEdges.push(
                new Edge(vertices[4], vertices[5]),
                new Edge(vertices[5], vertices[6]),
                new Edge(vertices[6], vertices[7]),
                new Edge(vertices[7], vertices[4])
            );
            this._imageEdges.push(
                new Edge(vertices[0], vertices[4]),
                new Edge(vertices[1], vertices[5]),
                new Edge(vertices[2], vertices[6]),
                new Edge(vertices[3], vertices[7])
            );
        }
        return this._imageEdges;
    }

    /**
     * Method to calculate the intersection between this Image and a projection plane.\
     */
    public crossSection(
        toProjection: ReadonlyMat4
    ): VertexArrayObject | undefined {
        const vertexCoordinates: number[] = [];
        const textureCoordinates: number[] = [];
        const indices: number[] = [];
        const intersections = SliceMesh.intersections(
            this.imageEdges,
            toProjection
        );
        if (intersections && intersections.length > 0) {
            const buffers = VerticesToArrays(intersections);
            indices.push(
                ...SliceMesh.tesselate(buffers[0], vertexCoordinates.length / 2)
            );
            vertexCoordinates.push(...buffers[0]);
            textureCoordinates.push(...buffers[1]);
        }
        if (indices.length === 0) {
            //if there's not a single intersection point, there is nothing to be rendered.
            return undefined;
        }
        const vao = new VertexArrayObject();
        vao.bind();
        vao.createAttribute(0, gl.FLOAT, vertexCoordinates, 2);
        vao.createAttribute(1, gl.FLOAT, textureCoordinates, 3);
        vao.createIndexBuffer(indices);
        vao.unbind();
        return vao;
    }

    /**
     * Method to get the value of the image at a given patient coordinate.
     * @param patientCoords The patient coordinate at which to get the image value.
     * @returns The image value at the given patient coordinate or null if the coordinate is outside the bounds of the image.
     */
    public getImageObjectValueByCoordinate(
        patientCoords: ReadonlyVec3
    ): number | null {
        if (!this.textureData || this.textureData.length === 0) {
            return null;
        }
        const indices = this.patientCoordsToImageIndices(patientCoords);
        const x = Math.floor(indices[0]);
        const y = Math.floor(indices[1]);
        const z = indices.length > 2 ? Math.floor(indices[2]) : 0;
        const { sizeX, sizeY, sizeZ } = this._parent.grid;
        if (x < 0 || y < 0 || z < 0 || x >= sizeX || y >= sizeY || z >= sizeZ) {
            return null;
        }
        const index = z * sizeX * sizeY + y * sizeX + x;
        const value = this.textureData[index];
        if (isNaN(value) || value === undefined) {
            return null;
        }
        return value;
    }

    // Calculate a step along the image grid from the position and direction
    public calculateStep(pos: ReadonlyVec3, dir: ReadonlyVec3): ReadonlyVec3 {
        const stepInGrid = Math.max(
            Math.abs(
                vec3.dot(
                    vec3.scale(vec3.create(), dir, this._parent.grid.resZ),
                    this._parent.grid.getUnitZ()
                )
            ),
            Math.abs(
                vec3.dot(
                    vec3.scale(vec3.create(), dir, this._parent.grid.resY),
                    this._parent.grid.getUnitY()
                )
            ),
            Math.abs(
                vec3.dot(
                    vec3.scale(vec3.create(), dir, this._parent.grid.resX),
                    this._parent.grid.getUnitX()
                )
            )
        );
        const step = vec3.scale(vec3.create(), dir, stepInGrid);
        if (!this._parent.grid.isAxisAligned()) {
            return step;
        }
        const posInVoxelspace = this._parent.grid.toVoxelSpace(
            vec3.add(vec3.create(), pos, step)
        );
        const posInPatientspace = this._parent.grid.toWorldSpace(
            vec3.fromValues(
                Math.round(posInVoxelspace[0]),
                Math.round(posInVoxelspace[1]),
                Math.round(posInVoxelspace[2])
            )
        );

        return vec3.sub(vec3.create(), posInPatientspace, pos);
    }

    public delete(): boolean {
        gl.deleteTexture(this.texture);
        return true;
    }

    private initializeTexture(): WebGLTexture {
        const texture = gl.createTexture();
        if (!texture) {
            throw new Error(`Unable to create WebGLTexture for ImageObject.`);
        }
        gl.bindTexture(gl.TEXTURE_3D, texture);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_BASE_LEVEL, 0);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAX_LEVEL, 0);

        // gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        // gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
        return texture;
    }

    private updateTexture(texture: Float32Array, grid: Grid) {
        this.textureData = texture;
        gl.bindTexture(gl.TEXTURE_3D, this.texture);
        gl.texImage3D(
            gl.TEXTURE_3D, // target
            0, // level
            gl.R16F, // internalformat
            grid.sizeX, // width
            grid.sizeY, // height
            grid.sizeZ, // depth
            0, // border
            gl.RED, // format
            gl.FLOAT, // type
            this.textureData // pixel
        );
    }

    // TODO: retain this as it will be useful for progressively updating the image texture later on
    private addSliceToTexture(index: number, imageSlice: ImageSlice) {
        if (!this.textureData) {
            throw new Error(
                `Cannot add slice to texture, texture data is not initialized.`
            );
        }
        const newArray = new Float32Array(
            this.textureData.length + imageSlice.pixelData.length
        );
        if (index === 0) {
            newArray.set(imageSlice.pixelData);
            newArray.set(this.textureData, imageSlice.pixelData.length);
        } else if (index === this.parent.grid.sizeZ - 1) {
            newArray.set(this.textureData);
            newArray.set(imageSlice.pixelData, this.textureData.length);
        } else {
            const before = imageSlice.pixelData.length * index;
            newArray.set(this.textureData.slice(0, before), 0);
            newArray.set(imageSlice.pixelData, before);
            newArray.set(
                this.textureData.slice(before),
                before + imageSlice.pixelData.length
            );
        }
        this.textureData = newArray;
        gl.bindTexture(gl.TEXTURE_3D, this.texture);
        //Note: Uses texImage3D because we don't know the size of the scan ahead of time.
        //If we knew how many slices there are, we could allocate big enough texture and use texSubImage3D.
        gl.texImage3D(
            gl.TEXTURE_3D, // target
            0, // level
            gl.R16F, // internalformat
            imageSlice.cols, // width
            imageSlice.rows, // height
            this.parent.grid.sizeZ, // depth
            0, // border
            gl.RED, // format
            gl.FLOAT, // type
            this.textureData // pixel
        );
    }

    private readonly _boundingBox: RotatedBoundingBox;

    public get boundingBox(): RotatedBoundingBox {
        return this._boundingBox;
    }

    public bindTexture(): void {
        gl.bindTexture(gl.TEXTURE_3D, this.texture);
    }
}

/**
 * {@link ImageObjectSliceStorage} is a storage class that inherits from {@link SliceSource}.\
 * It is used to store the information of slices loaded on a scan.
 */
class ImageObjectSliceStorage extends SliceSource<SliceData> {
    /**
     * Rotation from ImageSpace to PatientSpace.
     */
    private readonly _rotation: ReadonlyMat3;

    /**
     * The number of pixels along the horizontal direction.
     * The size of this {@link ImageObject}'s texture content along x-axis.
     */
    private readonly _width: number;

    /**
     * The number of pixels along the vertical direction.
     * The size of this {@link ImageObject}'s texture content along y-axis.
     */
    private readonly _height: number;

    /**
     * The {@link ImageSlice.pixelSpacing pixel spacing} along the planar x and y-axes.\
     * The PixelSpacing in these directions is constant and shared between all {@link ImageSlice slices}.\
     * It's the physical distance in between the center of each pixel.
     */
    private readonly pixelSpacing: ReadonlyVec2;

    /**
     * The minimum normal position.\
     * This is the position of the center point of the bottom most slice along the Image's normal direction.
     */
    private minimumNormal: number;

    /**
     * The maximum normal position.\
     * This is the position of the center point of the top most slice along the Image's normal direction.
     */
    private maximumNormal: number;

    /**
     * PatientSpaceCoordinates of the scan's center point.\
     */
    private readonly _center: vec3;

    /**
     * The planar coordinates well alinged with the ImageSpace.\
     * We can do vec3(planarCenter, centerNormalPosition) * this._rotation to get center point in PatientSpace.
     */
    private readonly planarCenter: ReadonlyVec2;

    /**
     * {@link mat4 Transformation} that may be applied to convert point from PatientSpace to ImageSpace.\
     * ImageSpace coordinates are in between -1 and 1 for patient space coordinates within the Image.
     */
    private readonly patientToImage: mat4;

    /**
     * {@link mat4 Transformation} that may be applied to convert point from ImageSpace to PatientSpace.\
     * ImageSpace coordinates are in between -1, 1 for points within the Image.
     */
    private readonly imageToPatient: mat4;

    public constructor(grid: Grid, slice: ImageSlice) {
        super(grid.resZ);
        this._width = grid.sizeX;
        this._height = grid.sizeY;
        this.pixelSpacing = vec2.clone(slice.pixelSpacing);

        this._rotation = mat3.clone(slice.imageOrientation);

        const planarDelta = vec2.fromValues(
            slice.cols / 2 - 0.5,
            slice.rows / 2 - 0.5
        );
        vec2.multiply(planarDelta, planarDelta, slice.pixelSpacing);
        const pT = vec3.transformMat3(
            vec3.create(),
            slice.imagePosition,
            this.invRotation
        );
        this.planarCenter = vec2.add(
            vec2.create(),
            planarDelta,
            vec2.fromValues(pT[0], pT[1])
        );
        const delta = vec3.transformMat3(
            vec3.create(),
            vec3.fromValues(planarDelta[0], planarDelta[1], 0),
            this._rotation
        );

        const normalPosition = vec3.transformMat3(
            vec3.create(),
            slice.imagePosition,
            this.invRotation
        )[2];
        this.minimumNormal = this.maximumNormal = normalPosition;

        this._center = vec3.add(vec3.create(), slice.imagePosition, delta);

        this.imageToPatient = mat4.fromRotationTranslationScale(
            mat4.create(),
            quat.fromMat3(quat.create(), this._rotation),
            this._center,
            vec3.fromValues(
                this._width * 0.5 * this.pixelSpacing[0],
                this._height * 0.5 * this.pixelSpacing[1],
                1
            )
        );
        this.patientToImage = mat4.invert(mat4.create(), this.imageToPatient);
    }

    public resetFromImage3D(image: Image3D) {
        const invRotation = mat3.transpose(
            mat3.create(),
            image.grid.getOrientation()
        );
        this.minimumNormal = vec3.transformMat3(
            vec3.create(),
            image.grid.getPosition(),
            invRotation
        )[2];
        this.maximumNormal =
            (image.grid.sizeZ - 1) * image.grid.resZ + this.minimumNormal;
        this.updateCenter();
        this.clearSlices();
        for (let z = 0; z < image.grid.sizeZ; z++) {
            this.addSlice(
                new SliceData(
                    this.minimumNormal + z * image.grid.resZ,
                    image.grid.resZ
                )
            );
        }
    }

    /**
     * Method to update the {@link _center center} coordinates of the {@link ImageObject}.\
     * This also updates the {@link imageToPatient} and {@link patientToImage}-matrices, as the translation of those
     * depend on the {@link _center center} coordinates.
     */
    private updateCenter(): void {
        const normalCenter = (this.minimumNormal + this.maximumNormal) * 0.5;
        vec3.transformMat3(
            this._center,
            vec3.fromValues(
                this.planarCenter[0],
                this.planarCenter[1],
                normalCenter
            ),
            this._rotation
        );

        mat4.fromRotationTranslationScale(
            this.imageToPatient,
            quat.fromMat3(quat.create(), this._rotation),
            this._center,
            vec3.fromValues(
                this.widthMm * 0.5,
                this.heightMm * 0.5,
                (this.maximumNormal - this.minimumNormal) * 0.5
            )
        );

        mat4.invert(this.patientToImage, this.imageToPatient);
    }

    public get rotation(): ReadonlyMat3 {
        return this._rotation;
    }

    public get center(): ReadonlyVec3 {
        return this._center;
    }

    public get widthMm(): number {
        return this._width * this.pixelSpacing[0];
    }

    public get widthPx(): number {
        return this._width;
    }

    public get heightMm(): number {
        return this._height * this.pixelSpacing[1];
    }

    public get heightPx(): number {
        return this._height;
    }

    public get sizeMm(): ReadonlyVec3 {
        return vec3.fromValues(
            this._width,
            this._height,
            this.maximumNormal - this.minimumNormal
        );
    }

    public patientCoordsToImageIndices(patientCoords: vec3): vec2 {
        const imgSpaceCoords = this.toImageSpace(patientCoords);
        const indices = vec2.fromValues(imgSpaceCoords[0], imgSpaceCoords[1]);
        let convertedIndices = vec2.add(
            indices,
            indices,
            vec2.fromValues(1, 1)
        );
        convertedIndices = vec2.scale(convertedIndices, convertedIndices, 0.5);
        return vec2.multiply(
            convertedIndices,
            convertedIndices,
            vec2.fromValues(this._width, this._height)
        );
    }

    public toImageSpace(patient: ReadonlyVec3): vec3 {
        return vec3.transformMat4(vec3.create(), patient, this.patientToImage);
    }

    /**
     * Method to convert from ImageSpace coordinates to PatientSpaceCoordinates.
     * ImageSpace coordinates are in between -1 and 1.
     * @param image
     */
    public toPatientSpace(image: ReadonlyVec3): vec3 {
        return vec3.transformMat4(vec3.create(), image, this.imageToPatient);
    }

    public generateVertex(x: 1 | -1, y: 1 | -1, normalPosition: number): vec3 {
        const v = vec3.fromValues(x, y, normalPosition);
        return vec3.transformMat4(v, v, this.imageToPatient);
    }
}
